import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Ayuda, Boton, Buscador, Chip, Filtro, IconoMas, Menu, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { useTituloPantalla } from '../../layout/titulo';
import { usePermisos } from '../../permisos/usePermiso';
import {
  normalizar,
  useCambiosCatalogos,
  useCambiosTipos,
  useCategorias,
  useEspecialidades,
  useTipo,
  useTipos,
  type Categoria,
  type Especialidad,
} from './datos';
import { EliminarCategoria } from './EliminarCategoria';
import { EliminarEspecialidad } from './EliminarEspecialidad';
import { PanelCampos } from './PanelCampos';
import { VentanaCategoria, type EdicionCategoria } from './VentanaCategoria';
import { VentanaEspecialidad, type EdicionEspecialidad } from './VentanaEspecialidad';
import { VentanaTipoDeActivo } from './VentanaTipoDeActivo';

// Configuraciones › Catálogos (#154): «Configuraciones - Catálogos» del Figma.
// Los catálogos de apoyo —los que se eligen de un combo, nadie los cita por
// número y les basta estar activos o no— en una sola pantalla, con el diseño de
// Marcas y modelos: los catálogos a la izquierda, los ítems del elegido a la
// derecha. En el celular las columnas se apilan.
//
// - **Categorías** de productos y servicios, y **especialidades** del personal:
//   se agregan, se renombran, se desactivan y se eliminan si nunca se usaron.
// - **Tipos de activo**: tenían pantalla propia (#117) y se mantienen acá desde
//   #154. Sus campos se editan en un panel por la derecha. No se eliminan: los
//   activos ya registrados los apuntan.
//
// El Figma solo dibujó las categorías; los otros dos siguen ese diseño.
//
// Cada catálogo sale solo para quien tiene su permiso, el mismo que pide su API.

type Clave = 'categorias' | 'especialidades' | 'tipos';
type Estado = 'todos' | 'activos' | 'inactivos';

/** Lo que dibuja una fila, sea del catálogo que sea. */
interface Fila {
  id: string;
  nombre: string;
  activo: boolean;
  /** «Usada en 14», «Sin usar». */
  uso: string;
  opciones: ElementoMenu[];
}

export function ListadoCatalogos() {
  const { t } = useTraduccion();
  const { puede, cargando } = usePermisos();
  useTituloPantalla(t('admin.catalogos.titulo'));

  const permitidos: Record<Clave, boolean> = {
    categorias: puede('catalog.edit'),
    especialidades: puede('staff.manage'),
    // Las dos que pedía la pantalla de tipos: escribir es configuración, y sus
    // consultas son del contexto de clientes y activos.
    tipos: puede('settings.edit') && puede('customer.edit'),
  };

  const [elegido, setElegido] = useState<Clave | null>(null);
  const [texto, setTexto] = useState('');
  const [estado, setEstado] = useState<Estado>('todos');

  const [edicionCategoria, setEdicionCategoria] = useState<EdicionCategoria>(null);
  const [eliminarCategoria, setEliminarCategoria] = useState<Categoria | null>(null);
  const [edicionEspecialidad, setEdicionEspecialidad] = useState<EdicionEspecialidad>(null);
  const [eliminarEspecialidad, setEliminarEspecialidad] = useState<Especialidad | null>(null);
  const [nuevoTipo, setNuevoTipo] = useState(false);
  const [tipoEnEdicion, setTipoEnEdicion] = useState<string | null>(null);
  const [tipoConCampos, setTipoConCampos] = useState<string | null>(null);

  const categorias = useCategorias(permitidos.categorias);
  const especialidades = useEspecialidades(permitidos.especialidades);
  const tipos = useTipos(permitidos.tipos);
  // Editar un tipo pide su versión, que la lista no trae: se lee el tipo.
  const fichaEnEdicion = useTipo(tipoEnEdicion ?? undefined);

  const cambios = useCambiosCatalogos();
  const cambiosTipos = useCambiosTipos();
  const { hacer, problema, setProblema } = useAccionDeFila();

  const catalogos: { clave: Clave; nombre: string; conteo: string }[] = [
    {
      clave: 'categorias' as const,
      nombre: t('admin.catalogos.categorias.titulo'),
      conteo: categorias.data ? t('admin.catalogos.categorias.conteo', { n: categorias.data.length }) : '',
    },
    {
      clave: 'especialidades' as const,
      nombre: t('admin.catalogos.especialidades.titulo'),
      conteo: especialidades.data
        ? t('admin.catalogos.especialidades.conteo', { n: especialidades.data.length })
        : '',
    },
    {
      clave: 'tipos' as const,
      nombre: t('admin.catalogos.tipos.titulo'),
      conteo: tipos.data ? t('admin.catalogos.tipos.conteo', { n: tipos.data.length }) : '',
    },
  ].filter((c) => permitidos[c.clave]);

  const actual = elegido ?? catalogos[0]?.clave ?? null;

  function elegir(clave: Clave) {
    // Un filtro —o un error— de categorías no significa nada en especialidades.
    setElegido(clave);
    setTexto('');
    setEstado('todos');
    setProblema(null);
    setTipoEnEdicion(null);
  }

  // «Editar» un tipo espera a leerlo, porque la lista no trae su versión. Si no
  // se pudo —sin red, o ya no existe—, se dice en vez de quedarse sin hacer nada.
  const tipoSinLeer = tipoEnEdicion !== null && (fichaEnEdicion.isError || fichaEnEdicion.data === null);

  function editarTipo(id: string) {
    setTipoEnEdicion(id);
    // Otra vez el mismo que falló: la consulta ya está en caché con su error.
    if (id === tipoEnEdicion) void fichaEnEdicion.refetch();
  }

  // ------------------------------------------------------------ Las filas

  function filasDeCategorias(): Fila[] {
    return (categorias.data ?? []).map((c) => {
      const nombre = { nombre: c.name };
      const id = String(c.id);
      return {
        id,
        nombre: c.name,
        activo: c.isActive,
        uso: Number(c.itemCount) > 0 ? t('admin.catalogos.categorias.usada', { n: Number(c.itemCount) }) : t('admin.catalogos.sinUsar'),
        opciones: [
          { etiqueta: t('admin.catalogos.menu.editar'), onSeleccionar: () => setEdicionCategoria({ tipo: 'editar', categoria: c }) },
          c.isActive
            ? {
                etiqueta: t('admin.catalogos.menu.desactivar'),
                onSeleccionar: () =>
                  void hacer(cambios.cambiarEstadoCategoria(id, false), t('admin.catalogos.categorias.aviso.desactivada', nombre)),
              }
            : {
                etiqueta: t('admin.catalogos.menu.activar'),
                onSeleccionar: () =>
                  void hacer(cambios.cambiarEstadoCategoria(id, true), t('admin.catalogos.categorias.aviso.activada', nombre)),
              },
          // Solo la que nada usa, ni un ítem eliminado: la API rechazaría las demás.
          ...(c.isDeletable
            ? [{ etiqueta: t('admin.catalogos.menu.eliminar'), peligro: true, onSeleccionar: () => setEliminarCategoria(c) }]
            : []),
        ],
      };
    });
  }

  function filasDeEspecialidades(): Fila[] {
    return (especialidades.data ?? []).map((e) => {
      const nombre = { nombre: e.name };
      const id = String(e.id);
      return {
        id,
        nombre: e.name,
        activo: e.isActive,
        uso:
          Number(e.staffCount) > 0
            ? t('admin.catalogos.especialidades.usada', { n: Number(e.staffCount) })
            : t('admin.catalogos.sinUsar'),
        opciones: [
          { etiqueta: t('admin.catalogos.menu.editar'), onSeleccionar: () => setEdicionEspecialidad({ tipo: 'editar', especialidad: e }) },
          e.isActive
            ? {
                etiqueta: t('admin.catalogos.menu.desactivar'),
                onSeleccionar: () =>
                  void hacer(
                    cambios.cambiarEstadoEspecialidad(id, false),
                    t('admin.catalogos.especialidades.aviso.desactivada', nombre),
                  ),
              }
            : {
                etiqueta: t('admin.catalogos.menu.activar'),
                onSeleccionar: () =>
                  void hacer(
                    cambios.cambiarEstadoEspecialidad(id, true),
                    t('admin.catalogos.especialidades.aviso.activada', nombre),
                  ),
              },
          ...(e.isDeletable
            ? [{ etiqueta: t('admin.catalogos.menu.eliminar'), peligro: true, onSeleccionar: () => setEliminarEspecialidad(e) }]
            : []),
        ],
      };
    });
  }

  function filasDeTipos(): Fila[] {
    return (tipos.data ?? []).map((tipo) => {
      const nombre = { nombre: tipo.name };
      const id = String(tipo.id);
      return {
        id,
        nombre: tipo.name,
        activo: tipo.isActive,
        uso: Number(tipo.assetCount) > 0 ? t('admin.catalogos.tipos.usado', { n: Number(tipo.assetCount) }) : t('admin.catalogos.sinUsar'),
        opciones: [
          { etiqueta: t('admin.catalogos.menu.editar'), onSeleccionar: () => editarTipo(id) },
          { etiqueta: t('admin.catalogos.menu.campos'), onSeleccionar: () => setTipoConCampos(id) },
          tipo.isActive
            ? {
                etiqueta: t('admin.catalogos.menu.desactivar'),
                onSeleccionar: () => void hacer(cambiosTipos.cambiarEstado(id, false), t('admin.tipos.aviso.desactivado', nombre)),
              }
            : {
                etiqueta: t('admin.catalogos.menu.activar'),
                onSeleccionar: () => void hacer(cambiosTipos.cambiarEstado(id, true), t('admin.tipos.aviso.activado', nombre)),
              },
        ],
      };
    });
  }

  const consulta = actual === 'categorias' ? categorias : actual === 'especialidades' ? especialidades : tipos;
  const todas =
    actual === 'categorias' ? filasDeCategorias() : actual === 'especialidades' ? filasDeEspecialidades() : filasDeTipos();

  const buscado = normalizar(texto);
  const filas = todas.filter(
    (f) =>
      normalizar(f.nombre).includes(buscado) &&
      (estado === 'todos' || (estado === 'activos' ? f.activo : !f.activo)),
  );
  const hayFiltros = buscado !== '' || estado !== 'todos';
  const inactivas = todas.filter((f) => !f.activo).length;
  // «Inactiva» para categorías y especialidades; «inactivo» para los tipos.
  const masculino = actual === 'tipos';

  function nuevo() {
    if (actual === 'categorias') setEdicionCategoria({ tipo: 'nueva' });
    if (actual === 'especialidades') setEdicionEspecialidad({ tipo: 'nueva' });
    if (actual === 'tipos') setNuevoTipo(true);
  }

  const textos = actual
    ? {
        categorias: {
          titulo: t('admin.catalogos.categorias.titulo'),
          resumen: t('admin.catalogos.categorias.conteo', { n: todas.length }),
          nuevo: t('admin.catalogos.categorias.nueva'),
          vacio: t('admin.catalogos.categorias.vacio'),
        },
        especialidades: {
          titulo: t('admin.catalogos.especialidades.titulo'),
          resumen: t('admin.catalogos.especialidades.conteo', { n: todas.length }),
          nuevo: t('admin.catalogos.especialidades.nueva'),
          vacio: t('admin.catalogos.especialidades.vacio'),
        },
        tipos: {
          titulo: t('admin.catalogos.tipos.titulo'),
          resumen: t('admin.catalogos.tipos.conteo', { n: todas.length }),
          nuevo: t('admin.tipos.nuevo'),
          vacio: t('admin.catalogos.tipos.vacio'),
        },
      }[actual]
    : null;

  return (
    <>
      <div className="titulo-con-ayuda">
        <h1>{t('admin.catalogos.titulo')}</h1>
        <Ayuda tema={t('admin.catalogos.titulo')} texto={t('admin.catalogos.info')} tono="titulo" />
      </div>

      {problema && <AvisoError aviso={{ titulo: t('admin.catalogos.error.titulo'), texto: problema }} />}
      {tipoSinLeer && (
        <AvisoError aviso={{ titulo: t('admin.catalogos.error.titulo'), texto: t('ui.listado.error.texto') }} />
      )}

      {cargando ? (
        <p className="marcas__nota">{t('app.cargando')}</p>
      ) : actual === null || textos === null ? (
        <p className="marcas__nota">{t('admin.catalogos.sinPermiso')}</p>
      ) : (
        <div className="catalogos">
          <section className="catalogos__tarjeta" aria-labelledby="catalogos-lista">
            <header className="catalogos__encabezado">
              <h2 id="catalogos-lista">{t('admin.catalogos.lista.titulo')}</h2>
              <p className="catalogos__resumen">{t('admin.catalogos.lista.texto')}</p>
            </header>
            <ul className="catalogos__catalogos">
              {catalogos.map((c) => (
                <li key={c.clave}>
                  <button
                    type="button"
                    className={`catalogos__catalogo${c.clave === actual ? ' catalogos__catalogo--elegido' : ''}`}
                    aria-pressed={c.clave === actual}
                    onClick={() => elegir(c.clave)}
                  >
                    <span>
                      <span className="catalogos__nombre">{c.nombre}</span>
                      {c.conteo && <span className="catalogos__resumen">{c.conteo}</span>}
                    </span>
                    <span aria-hidden="true">›</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          <section className="catalogos__tarjeta" aria-labelledby="catalogos-items">
            <header className="catalogos__encabezado catalogos__encabezado--con-boton">
              <div>
                <h2 id="catalogos-items">{textos.titulo}</h2>
                <p className="catalogos__resumen">
                  {inactivas > 0
                    ? `${textos.resumen} · ${t(masculino ? 'admin.catalogos.inactivos' : 'admin.catalogos.inactivas', { n: inactivas })}`
                    : textos.resumen}
                </p>
              </div>
              <Boton icono={<IconoMas />} onClick={nuevo}>
                {textos.nuevo}
              </Boton>
            </header>

            <div className="marcas__filtros">
              <Buscador valor={texto} onCambiar={setTexto} placeholder={t('admin.catalogos.buscar')} />
              <Filtro
                etiqueta={t('admin.catalogos.filtro.estado')}
                valor={estado}
                onCambiar={(valor) => setEstado(valor as Estado)}
                opciones={[
                  { valor: 'todos', etiqueta: t('admin.catalogos.filtro.todos') },
                  { valor: 'activos', etiqueta: t('admin.catalogos.filtro.activos') },
                  { valor: 'inactivos', etiqueta: t('admin.catalogos.filtro.inactivos') },
                ]}
              />
            </div>

            {consulta.isPending ? (
              <p className="marcas__nota">{t('app.cargando')}</p>
            ) : consulta.isError ? (
              <div className="marcas__nota">
                <p>{t('ui.listado.error.texto')}</p>
                <Boton variante="secundario" onClick={() => void consulta.refetch()}>
                  {t('app.reintentar')}
                </Boton>
              </div>
            ) : filas.length === 0 ? (
              <div className="marcas__nota">
                <p className="marcas__nota-titulo">{hayFiltros ? t('ui.listado.sinResultados.titulo') : textos.vacio}</p>
                {hayFiltros ? (
                  <>
                    <p>{t('ui.listado.sinResultados.texto')}</p>
                    <Boton
                      variante="secundario"
                      onClick={() => {
                        setTexto('');
                        setEstado('todos');
                      }}
                    >
                      {t('ui.listado.limpiarFiltros')}
                    </Boton>
                  </>
                ) : (
                  <Boton icono={<IconoMas />} onClick={nuevo}>
                    {textos.nuevo}
                  </Boton>
                )}
              </div>
            ) : (
              <ul className="catalogos__items">
                {filas.map((f) => (
                  <li key={f.id} className={`catalogos__item${f.activo ? '' : ' catalogos__item--inactivo'}`}>
                    <span className="catalogos__item-nombre">
                      {f.nombre}
                      {!f.activo && <Chip>{t(masculino ? 'admin.catalogos.inactivo' : 'admin.catalogos.inactiva')}</Chip>}
                    </span>
                    <span className="catalogos__uso">{f.uso}</span>
                    <MenuFila elementos={f.opciones} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      <VentanaCategoria edicion={edicionCategoria} onCerrar={() => setEdicionCategoria(null)} />
      <EliminarCategoria categoria={eliminarCategoria} onCerrar={() => setEliminarCategoria(null)} />
      <VentanaEspecialidad edicion={edicionEspecialidad} onCerrar={() => setEdicionEspecialidad(null)} />
      <EliminarEspecialidad especialidad={eliminarEspecialidad} onCerrar={() => setEliminarEspecialidad(null)} />
      <VentanaTipoDeActivo
        edicion={nuevoTipo ? 'nuevo' : tipoEnEdicion && fichaEnEdicion.data ? 'editar' : null}
        {...(fichaEnEdicion.data && { ficha: fichaEnEdicion.data })}
        onCerrar={() => {
          setNuevoTipo(false);
          setTipoEnEdicion(null);
        }}
        // Recién creado no tiene campos: se abren para agregarle los suyos.
        onCreado={setTipoConCampos}
      />
      <PanelCampos tipoId={tipoConCampos} onCerrar={() => setTipoConCampos(null)} />
    </>
  );
}

/** El «•••» de cada fila, el mismo del listado. */
function MenuFila({ elementos }: { elementos: readonly ElementoMenu[] }) {
  const { t } = useTraduccion();
  return (
    <Menu
      elementos={elementos}
      disparador={({ alternar, atributos }) => (
        <button
          type="button"
          className="ui-tabla__mas"
          aria-label={t('ui.listado.accionesFila')}
          onClick={alternar}
          {...atributos}
        >
          •••
        </button>
      )}
    />
  );
}
